import { useState, useEffect, useCallback } from "react";
import { ResultadosView } from "./ResultadosView.jsx";
import { OperacionesView } from "./OperacionesView.jsx";

const API = process.env.REACT_APP_API_URL || "https://web-production-61310.up.railway.app";

const C = { bg: "#0a0a0a", card: "#111", border: "#1e1e1e", accent: "#e8700a", text: "#f5f5f5", dim: "#666" };

const LOCALE_GROUPS = [
  { region: "AR", color: "#e8700a", locales: [
    { nombre: "Organyco",      slug: "organyco" },
    { nombre: "Yeca Nordelta", slug: "yeca-nordelta" },
    { nombre: "Yeca Bodegón",  slug: "yeca-bodegon" },
    { nombre: "Bien de Bien",  slug: "bien-de-bien" },
  ]},
  { region: "USA", color: "#3b82f6", locales: [
    { nombre: "Negroni Midtown",  slug: "negroni-midtown" },
    { nombre: "Negroni Weston",   slug: "negroni-weston" },
    { nombre: "Italica Midtown",  slug: "italica-midtown" },
    { nombre: "Italica Aventura", slug: "italica-aventura" },
  ]},
  { region: "ES", color: "#22c55e", locales: [
    { nombre: "Golf El Prat", slug: "golf-el-prat" },
  ]},
];

const VIEWS = ["Resultados", "Operaciones"];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysAgoStr(n) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

const PRESETS = [
  { label: "Hoy",  get: () => ({ from: todayStr(),     to: todayStr() }) },
  { label: "7d",   get: () => ({ from: daysAgoStr(6),  to: todayStr() }) },
  { label: "30d",  get: () => ({ from: daysAgoStr(29), to: todayStr() }) },
  { label: "90d",  get: () => ({ from: daysAgoStr(89), to: todayStr() }) },
];

function Sidebar({ collapsed, onToggle, selectedSlug, onSelect }) {
  const w = collapsed ? 44 : 180;
  return (
    <div style={{ width: w, minWidth: w, background: C.card, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", transition: "width .2s ease", overflow: "hidden", position: "relative" }}>
      <button onClick={onToggle} style={{ background: "none", border: "none", cursor: "pointer", padding: "12px", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-end", color: C.dim }}>
        {collapsed ? "›" : "‹"}
      </button>
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {LOCALE_GROUPS.map(({ region, color, locales }) => (
          <div key={region} style={{ marginBottom: 8 }}>
            {!collapsed && (
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "2px", color, padding: "8px 12px 4px", textTransform: "uppercase" }}>{region}</div>
            )}
            {collapsed && <div style={{ height: 1, background: C.border, margin: "6px 8px" }} />}
            {locales.map(loc => {
              const active = loc.slug === selectedSlug;
              return (
                <button key={loc.slug} onClick={() => onSelect(loc)}
                  title={collapsed ? loc.nombre : undefined}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%", padding: collapsed ? "8px" : "7px 12px",
                    background: active ? `${color}18` : "none", border: "none", cursor: "pointer",
                    borderLeft: `2px solid ${active ? color : "transparent"}`,
                    justifyContent: collapsed ? "center" : "flex-start",
                  }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0, opacity: active ? 1 : 0.4 }} />
                  {!collapsed && <span style={{ fontSize: 12, color: active ? C.text : C.dim, fontWeight: active ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{loc.nombre}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DateRangePicker({ preset, from, to, onPreset, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {PRESETS.map(p => (
        <button key={p.label} onClick={() => onPreset(p)}
          style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, cursor: "pointer", border: `1px solid ${preset === p.label ? C.accent : C.border}`, background: preset === p.label ? `${C.accent}18` : "none", color: preset === p.label ? C.accent : C.dim }}>
          {p.label}
        </button>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 4 }}>
        <input type="date" value={from} onChange={e => onChange({ from: e.target.value, to })}
          style={{ fontSize: 11, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: "3px 7px", colorScheme: "dark" }} />
        <span style={{ color: C.dim, fontSize: 11 }}>→</span>
        <input type="date" value={to} onChange={e => onChange({ from, to: e.target.value })}
          style={{ fontSize: 11, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: "3px 7px", colorScheme: "dark" }} />
      </div>
    </div>
  );
}

function findLocale(slug) {
  for (const g of LOCALE_GROUPS) {
    const loc = g.locales.find(l => l.slug === slug);
    if (loc) return loc;
  }
  return LOCALE_GROUPS[0].locales[0];
}

export function InvestorDashboard({ slug }) {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(() => findLocale(slug));
  const [activeView, setActiveView] = useState("Resultados");
  const [preset, setPreset] = useState("30d");
  const [dateRange, setDateRange] = useState(PRESETS[2].get());

  const [summary, setSummary] = useState(null);
  const [salesByHour, setSalesByHour] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async (slug, from, to) => {
    setLoading(true); setError(null);
    setSummary(null); setSalesByHour(null); setTopProducts(null); setExpenses(null);
    const qs = `from_date=${from}&to_date=${to}`;
    try {
      const [sumRes, hoursRes, topRes, expRes] = await Promise.all([
        fetch(`${API}/dashboard/${slug}/summary?${qs}`),
        fetch(`${API}/dashboard/${slug}/sales-by-hour?${qs}`),
        fetch(`${API}/dashboard/${slug}/top-products?${qs}&limit=10`),
        fetch(`${API}/dashboard/${slug}/expenses?${qs}`),
      ]);
      const [sumData, hoursData, topData, expData] = await Promise.all([
        sumRes.ok ? sumRes.json() : null,
        hoursRes.ok ? hoursRes.json() : [],
        topRes.ok ? topRes.json() : [],
        expRes.ok ? expRes.json() : null,
      ]);
      setSummary(sumData); setSalesByHour(hoursData); setTopProducts(topData); setExpenses(expData);
    } catch (e) {
      setError("Error al cargar datos. Verificá la conexión.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(selectedLocale.slug, dateRange.from, dateRange.to);
  }, [selectedLocale, dateRange, fetchAll]);

  const handlePreset = (p) => { setPreset(p.label); setDateRange(p.get()); };
  const handleDateChange = (d) => { setPreset(null); setDateRange(d); };
  const handleLocaleSelect = (loc) => { setSelectedLocale(loc); };

  const currency = summary?.currency || "ARS";
  const localeGroup = LOCALE_GROUPS.find(g => g.locales.some(l => l.slug === selectedLocale.slug));
  const regionColor = localeGroup?.color || C.accent;

  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, color: C.text, fontFamily: "'Inter','Helvetica Neue',sans-serif", overflow: "hidden" }}>
      <style>{`* { box-sizing: border-box; margin: 0; padding: 0; } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }`}</style>

      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} selectedSlug={selectedLocale.slug} onSelect={handleLocaleSelect} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: regionColor }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{selectedLocale.nombre}</span>
          </div>
          <div style={{ display: "flex", gap: 2 }}>
            {VIEWS.map(v => (
              <button key={v} onClick={() => setActiveView(v)}
                style={{ fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 6, cursor: "pointer", border: `1px solid ${activeView === v ? C.accent : C.border}`, background: activeView === v ? `${C.accent}18` : "none", color: activeView === v ? C.accent : C.dim }}>
                {v}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <DateRangePicker preset={preset} from={dateRange.from} to={dateRange.to} onPreset={handlePreset} onChange={handleDateChange} />
          </div>
          <button onClick={() => fetchAll(selectedLocale.slug, dateRange.from, dateRange.to)}
            style={{ fontSize: 11, padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "none", color: C.dim, cursor: "pointer" }}>
            ↺
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{ background: "rgba(239,68,68,.1)", borderBottom: "1px solid rgba(239,68,68,.2)", padding: "8px 20px", fontSize: 12, color: "#ef4444" }}>
            {error}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {activeView === "Resultados" && (
            <ResultadosView summary={summary} expenses={expenses} currency={currency} loading={loading} />
          )}
          {activeView === "Operaciones" && (
            <OperacionesView summary={summary} salesByHour={salesByHour} topProducts={topProducts} currency={currency} loading={loading} />
          )}
        </div>

      </div>
    </div>
  );
}
